// #region import
import { getApp } from 'firebase/app'
import { addDoc, collection, deleteDoc, doc, DocumentReference, getDoc, getDocs, getFirestore, setDoc, updateDoc } from 'firebase/firestore'
import { getDownloadURL, getMetadata, getStorage, listAll, ref, uploadBytesResumable } from 'firebase/storage'
import Vue from 'vue'
import Vuex from 'vuex'
import { ADD_ALERT, CACHE, CALLS, SET_INSTANCE, SET_NAV_STATE } from './mutations'
// modules
import app from './app'
import authentication from './authentication'
import firestore from './firestore'
import functions from './functions'
import report from './report'
// #endregion

import { NavState } from '@/utils'

// #region use
Vue.use(Vuex)
// #endregion

export default new Vuex.Store({
  state: {
    cache: {
      documents: [],
      collections: [],
    },
    calls: 0,
    instance: undefined,
    dashboards: undefined,
    navState: NavState.Open,
    alerts: [],
  },
  mutations: {
    [CACHE](state, payload) {
      state.cache[payload.key][payload.reference] = payload.document
    },
    [CALLS](state, payload) {
      state.calls += payload
    },
    [SET_INSTANCE](state, payload) {
      state.instance = payload
    },
    [SET_NAV_STATE](state, payload) {
      state.navState = payload
    },
    [ADD_ALERT](state, payload) {
      state.alerts.push(payload)
    },
  },
  actions: {
    /**
     * Initialize the Store.
     * @async
     */
    initializeStore({ dispatch }) {
      return new Promise(async (resolve, reject) => {
        try {
          await dispatch('authentication/initializeModule', {}, { root: true })
          // ! this is a hot fix.
          // TODO this must be solved properly
          setTimeout(async () => {
            console.log(this.state.authentication.currentUser)
            if (this.state.authentication.currentUser) {
              await dispatch('app/initializeModule', {}, { root: true })
            }
            resolve()
          }, 500)
          await dispatch('firestore/initializeModule', {}, { root: true })
        } catch (reason) {
          reject()
        }
      })
    },
    fetchInstance({ dispatch, commit }) {
      return new Promise((resolve, reject) => {
        dispatch('getDocument', {
          reference: '_apps/tRIs9VwBhA3GcJc0a4eI', //TODO - remove hardcoded app document, make a collection called "_instance" with an array of apps
        })
          .then((documentSnapshot) => {
            let data = documentSnapshot.data()
            let promises = data.dashboards.map((value) => {
              return dispatch('getDocument', {
                reference: value.path,
              })
            })
            Promise.all(promises).then(async (documentsSnapshots) => {
              for (let i = 0; i < documentsSnapshots.length; i++) {
                data.dashboards[i] = await populateDocument(documentsSnapshots[i].data(), dispatch)
              }
              commit('SET_INSTANCE', {
                apps: [data],
              })
              resolve(data)
            })
          })
          .catch(reject)
      })
    },
    // eslint-disable-next-line no-empty-pattern
    getDoc({}, { reference }) {
      return doc(getFirestore(getApp('secondary')), reference)
    },
    // eslint-disable-next-line no-empty-pattern
    populateDocument({ dispatch }, { document, maxDepth = 1 }) {
      return populateDocument(document, dispatch, maxDepth || 1)
    },
    // eslint-disable-next-line no-empty-pattern
    uploadToStorage({}, { file, reference }) {
      return new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(ref(getStorage(getApp('secondary')), reference), file)
        uploadTask.on('state_changed', null, reject, () => {
          resolve(getMetadata(uploadTask.snapshot.ref))
        })
      })
    },
    // eslint-disable-next-line no-empty-pattern
    downloadFromStorage({}, { reference }) {
      return getDownloadURL(ref(getStorage(getApp('secondary')), reference))
    },
    // eslint-disable-next-line no-empty-pattern
    listStorage({}, { folderRef }) {
      return listAll(ref(getStorage(getApp('secondary')), folderRef))
    },
    // eslint-disable-next-line no-empty-pattern
    getMetadata({}, { reference }) {
      return getMetadata(reference)
    },
    // eslint-disable-next-line no-empty-pattern
    getStorageReference({}, { reference }) {
      return ref(getStorage(getApp('secondary')), reference)
    },
    rebuildCache({ dispatch, state }) {
      return new Promise((resolve, reject) => {
        let cache = {}
        let promises = []
        dispatch('getCollection', { reference: '_blueprints' }).then((querySnapshot) => {
          let blueprints = querySnapshot.docs.map((value) => {
            return value.data()
          })
          console.log('BLUEPRINTS', blueprints)
          blueprints.forEach((blueprint) => {
            if (blueprint.cache) {
              promises.push(
                dispatch('getCollection', { reference: blueprint.collection }).then((querySnapshot) => {
                  // cache[blueprint.collection] = querySnapshot.docs.map((value) => {
                  //   let data = value.data()
                  //   if (data.status == undefined || data.status == true) {
                  //     return { ...value.data(), reference: doc(getFirestore(getApp('secondary')), blueprint.collection, value.id) }
                  //   } else {
                  //     return {}
                  //   }
                  // })
                  cache[blueprint.collection] = []
                  for (let i = 0; i < querySnapshot.docs.length; i++) {
                    let data = querySnapshot.docs[i].data()
                    if ((data.status == undefined || data.status == true) && (data.available == undefined || data.available == true)) {
                      cache[blueprint.collection].push({ ...data, reference: doc(getFirestore(getApp('secondary')), blueprint.collection, querySnapshot.docs[i].id) })
                    }
                  }
                })
              )
            }
          })
          Promise.all(promises)
            .then(() => {
              return dispatch('updateDocument', { document: cache, reference: '_cache/CoGEnpNIVuexy5SNhojT' })
            })
            .then(() => {
              resolve()
            })
            .catch(reject)
        })
      })
    },
    addAlert({ commit }, payload) {
      commit('ADD_ALERT', payload)
    },
    addDocument({ dispatch }, { document, reference }) {
      return new Promise((resolve, reject) => {
        console.log('adding doc', document, reference)
        addDoc(collection(getFirestore(getApp('secondary')), reference), document)
          .then((res) => {
            dispatch('addAlert', {
              show: 3,
              dismissable: true,
              variant: 'success',
              message: `Creation was successful`,
            })
            resolve(res)
          })
          .catch(reject)
      })
    },
    addDocuments({ dispatch }, { documents, reference }) {
      let values = []
      documents.forEach((value) => {
        values.push(
          dispatch('addDocument', {
            document: value,
            reference: reference,
          })
        )
      })
      return Promise.all(values)
    },
    importDocument({ dispatch }, { document, reference }) {
      return new Promise((resolve, reject) => {
        setDoc(doc(getFirestore(getApp('secondary')), reference), document)
          .then(() => {
            dispatch('addAlert', {
              show: 3,
              dismissable: true,
              variant: 'success',
              messege: `Set was successful`,
            })
            resolve()
          })
          .catch(reject)
      })
    },
    updateDocument({ dispatch }, { document, reference }) {
      return new Promise((resolve, reject) => {
        console.log('updating doc', document, reference)
        updateDoc(doc(getFirestore(getApp('secondary')), reference), document)
          .then(() => {
            dispatch('addAlert', {
              show: 3,
              dismissable: true,
              variant: 'success',
              messege: `Edit was successful`,
            })
            resolve()
          })
          .catch(reject)
      })
    },
    updateDocuments({ dispatch }, { documents, reference }) {
      let values = []
      documents.forEach((value) => {
        values.push(
          dispatch('updateDocument', {
            document: value,
            reference: reference,
          })
        )
      })
      return Promise.all(values)
    },
    // eslint-disable-next-line no-empty-pattern
    getCollection({ state, commit }, { reference }) {
      return new Promise((resolve, reject) => {
        if (state.cache.collections[reference]) {
          resolve(state.cache.collections[reference])
        } else {
          getDocs(collection(getFirestore(getApp('secondary')), reference)).then((collectionSnapshot) => {
            commit('CALLS', collectionSnapshot.docs.length)
            commit('CACHE', {
              reference,
              key: 'collections',
              document: collectionSnapshot,
            })
            resolve(collectionSnapshot)
          })
        }
      })
    },
    getCollection2({ state, commit }, { reference }) {
      return new Promise((resolve, reject) => {
        function nextPage() {}
        function prevPage() {}
        resolve({
          nextPage,
          prevPage,
        })
      })
    },
    getCollections() {
      // TODO implement
    },
    // eslint-disable-next-line no-empty-pattern
    getDocument({ state, commit }, { reference }) {
      return new Promise((resolve, reject) => {
        if (state.cache.documents[reference]) {
          resolve(state.cache.documents[reference])
        } else {
          commit('CALLS', 1)
          getDoc(doc(getFirestore(getApp('secondary')), reference))
            .then((documentSnapshot) => {
              commit('CACHE', {
                reference,
                key: 'documents',
                document: documentSnapshot,
              })
              resolve(documentSnapshot)
            })
            .catch(reject)
        }
      })
    },
    getDocuments({ dispatch }, { references }) {
      let values = []
      references.forEach((value) => {
        values.push(
          dispatch('getDocument', {
            reference: value,
          })
        )
      })
      return Promise.all(values)
    },
    deleteDocument({ dispatch }, { reference }) {
      return new Promise((resolve, reject) => {
        deleteDoc(doc(getFirestore(getApp('secondary')), reference))
          .then(() => {
            dispatch('addAlert', {
              show: 3,
              dismissable: true,
              variant: 'danger',
              message: `Deletetion was successful`,
            })
            resolve()
          })
          .catch(reject)
      })
    },
    deleteDocuments({ dispatch }, { documents }) {
      let values = []
      documents.forEach((value) => {
        values.push(dispatch('deleteDocument', { reference: value }))
      })
      return Promise.all(values)
    },
  },
  modules: {
    authentication,
    firestore,
    functions,
    app,
    report,
  },
  getters: {
    getDashboards(state) {
      return state.instance.apps.map((x) => x.dashboards).flat()
    },
  },
})

function populateDocument(document, dispatch, maxDepth = 1) {
  const promises = []
  for (const [key, value] of Object.entries(document)) {
    promises.push(
      populate(value, dispatch).then((res) => {
        document[key] = res
      })
    )
  }
  return Promise.all(promises).then((data) => {
    if (maxDepth == 1) {
      return document
    }
    return populateDocument(document, dispatch, --maxDepth)
  })
}

async function populate(value, dispatch) {
  if (value === null || value === undefined) {
    return value
  }
  if (value instanceof DocumentReference) {
    const documentSnapshot = await dispatch('getDocument', {
      reference: value.path,
    })
    return documentSnapshot.data()
  }
  if (typeof value === 'object') {
    const promises = []
    if (!Array.isArray(value)) {
      for (const [key, _value] of Object.entries(value)) {
        promises.push(
          populate(_value, dispatch).then((res) => {
            value[key] = res
          })
        )
      }
    } else {
      for (let i = 0; i < value.length; i++) {
        promises.push(
          populate(value[i], dispatch).then((res) => {
            value[i] = res
          })
        )
      }
    }
    await Promise.all(promises)
  }
  return value
}
